import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { BuilderPublicShowroomCTACustomFieldConfig } from "api-types/builders/salesConfig";
import type { HomePlanContractSchemaUpdateOne } from "api-types/homePlanContracts/homePlanContractRequests";
import type { ClientHomePlanContractHydrated } from "api-types/homePlanContracts/homePlanContractResponses";
import type { HomePlanContractStatus } from "api-types/homePlanContracts/homePlanContractTypes";
import { HOME_PLAN_CATEGORY_LABELS } from "api-types/homePlans/homePlanFields";
import type { ClientHomePlanHydrated } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationPlan, ClientResolvedBrochureSource } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationHydrated } from "api-types/locations/locationsResponses";
import type { ClientLotDiscount, ClientLotHydrated } from "api-types/lots/lotsResponses";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { FetchError } from "common/types/fetchError";
import { formatDate } from "common/utils/dates";
import { validateEmail } from "common/utils/formValidators";
import Toggle from "lib/components/actions/Toggle";
import { CustomField } from "lib/components/builders/communities/plans/showroom/CustomFields";
import DownloadBrochureModal from "lib/components/design/DownloadBrochureModal";
import IconHomePlanCategory from "lib/components/typedIcons/IconHomePlanCategory";
import { getBrochureUserRole } from "lib/house/pdf/brochure/utils";
import { useUserContext } from "lib/state/UserContextProvider";
import type { FormEvent } from "react";
import { useState } from "react";
import Button from "ui/buttons/Button";
import IconButton from "ui/buttons/IconButton";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import LineCapsule from "ui/components/LineCapsule/LineCapsule";
import Loading from "ui/components/Loading";
import Modal from "ui/components/Modal";
import InputGroupNumber from "ui/forms/InputGroupNumber";
import InputGroupSelect from "ui/forms/InputGroupSelect";
import InputGroupText from "ui/forms/InputGroupText";
import useAppContext from "ui/hooks/useAppContext";
import IconEditSquare from "ui/icons/IconEditSquare";
import IconTrash from "ui/icons/IconTrash";
import OverflowMenu from "ui/popovers/OverflowMenu";
import { Message } from "ui/text/Message";
import getUserFullName from "../../../../../utils/userName";
import SectionHeader from "../SectionHeader";
import { formatContractPhoneNumber, validateContractPhoneNumber } from "./contractPhoneInput";
import styles from "./HomePlanContractList.module.scss";
import { useDeleteHomePlanContract, useHomePlanContracts, useUpdateHomePlanContract } from "./useHomePlanContractAPI";

// contract_status and home_plan_categories share the same values, so a contract's status reads
// with the same words the singular Customer Category uses everywhere else — including "Inactive"
// for closed, and Testing, which the category radio group has always offered.
const STATUS_OPTIONS = Object.entries(HOME_PLAN_CATEGORY_LABELS).map(([value, label]) => ({ value, label }));

/**
 * The contracts a plan is presented through, mirroring `presentedContracts` on the backend: the
 * ones still live, or all of them when none are. Deleting the only presented contract would leave
 * the plan with nothing to show, and the server refuses it, so the UI must not offer it either.
 */
function presentedContracts(contracts: ClientHomePlanContractHydrated[]): ClientHomePlanContractHydrated[] {
  const live = contracts.filter((contract) => contract.status !== "closed");
  return live.length > 0 ? live : contracts;
}

export function hasMultipleHomePlanContracts(enabled: boolean | undefined, contractCount: number | undefined): boolean {
  return !!enabled && (contractCount ?? 0) > 1;
}

interface HomePlanContractListProps {
  builderId: string;
  homePlanId: string;
  canEdit: boolean;
  variant: "table" | "detail";
  contractCount?: number;
  onChange?: () => void;
  // Only used by the "detail" variant, to offer a per-contract brochure download.
  homePlan?: ClientHomePlanHydrated;
  builder?: ClientBuilder;
  masterPlan?: ClientMasterPlan;
  location?: ClientLocationHydrated;
  locationPlan?: ClientLocationPlan;
  lot?: ClientLotHydrated;
  lotDiscounts?: ClientLotDiscount[];
  brochureSource?: ClientResolvedBrochureSource;
}

export default function HomePlanContractList({
  builderId,
  homePlanId,
  canEdit,
  variant,
  contractCount,
  onChange,
  homePlan,
  builder,
  masterPlan,
  location,
  locationPlan,
  lot,
  lotDiscounts,
  brochureSource,
}: HomePlanContractListProps) {
  const { contracts, data, loading, error } = useHomePlanContracts({ builderId, homePlanId });
  const { user, builderUser, enabledFeatures } = useUserContext();
  const resolvedBrochureSource = enabledFeatures.brochureSourceSelection ? brochureSource : undefined;
  // An unresolved source has no document to produce, so drop the item rather than
  // offering a download that falls back to the built-in brochure.
  const hasContractBrochureAccess = !!homePlan && !!enabledFeatures.homePlanContracts;
  const hasBrochureDocument = !resolvedBrochureSource || resolvedBrochureSource.source.sourceType !== "none";
  const canDownloadContractBrochure = hasContractBrochureAccess && hasBrochureDocument;

  const [contractToEdit, setContractToEdit] = useState<ClientHomePlanContractHydrated>();
  const [contractToDelete, setContractToDelete] = useState<ClientHomePlanContractHydrated>();
  const [contractToDownloadBrochure, setContractToDownloadBrochure] = useState<ClientHomePlanContractHydrated>();
  const presented = presentedContracts(contracts);
  const canDeleteContract = (contract: ClientHomePlanContractHydrated): boolean =>
    presented.length > 1 || !presented.some((presentedContract) => presentedContract.id === contract.id);
  const brochureUserRole = canDownloadContractBrochure
    ? getBrochureUserRole(user, homePlan, location, builderUser)
    : undefined;

  const renderTable = () => (
    <div className={styles.contracts}>
      <div className={styles.header} aria-hidden>
        <span>Customer</span>
        <span>Status</span>
        <span>Sales staff</span>
        <span>Created</span>
        {canEdit && <span />}
      </div>
      {contracts.map((contract) => (
        <div className={styles.contract} key={contract.id}>
          <span>{contract.customer?.name || "No customer"}</span>
          <span>{HOME_PLAN_CATEGORY_LABELS[contract.status]}</span>
          <span>{getUserFullName(contract.salesStaff ?? null) || "None"}</span>
          <span>{formatDate(new Date(contract.createdAt))}</span>
          {canEdit && (
            <span className={styles.actions}>
              <IconButton ariaLabel="Edit contract" btnStyle="tertiary" onClick={() => setContractToEdit(contract)}>
                <IconEditSquare size={14} />
              </IconButton>
              {canDeleteContract(contract) && (
                <IconButton
                  ariaLabel="Delete contract"
                  btnStyle="tertiary"
                  onClick={() => setContractToDelete(contract)}
                >
                  <IconTrash size={14} />
                </IconButton>
              )}
            </span>
          )}
        </div>
      ))}
    </div>
  );

  // The sidebar column is too narrow for the table grid, so this matches the other cards there
  // (CustomerInfo, PlanDocuments) instead.
  const renderCardList = () => (
    <div className={styles.cardList}>
      {contracts.map((contract) => {
        const menuItems: { children: string; onClick: () => void }[] = [];
        if (canEdit) {
          menuItems.push({ children: "Edit contract", onClick: () => setContractToEdit(contract) });
        }
        if (canEdit && canDeleteContract(contract)) {
          menuItems.push({ children: "Delete contract", onClick: () => setContractToDelete(contract) });
        }
        // Downloading a brochure is a read action, not an edit, so a view-only builder can still
        // do it even though the edit/delete items above are unavailable to them.
        if (canDownloadContractBrochure) {
          menuItems.push({ children: "Download Brochure", onClick: () => setContractToDownloadBrochure(contract) });
        }

        return (
          <div className={styles.card} key={contract.id}>
            <div className={styles.cardInfo}>
              <div className={styles.cardTitle}>
                <span>{contract.customer?.name || "No customer"}</span>
                <LineCapsule
                  label={HOME_PLAN_CATEGORY_LABELS[contract.status]}
                  icon={<IconHomePlanCategory category={contract.status} />}
                />
              </div>
              <span>Sales staff: {getUserFullName(contract.salesStaff ?? null) || "None"}</span>
              <span>Created {formatDate(new Date(contract.createdAt))}</span>
            </div>
            {menuItems.length > 0 && <OverflowMenu menuItems={menuItems} className={styles.cardMenu} />}
          </div>
        );
      })}
    </div>
  );

  const renderContracts = () => {
    if (loading) {
      return <Loading />;
    }
    if (error) {
      return <Message messageType="error">Contracts could not be loaded.</Message>;
    }
    if (contracts.length === 0) {
      return <Message>No contracts.</Message>;
    }

    return variant === "table" ? renderTable() : renderCardList();
  };

  return (
    <>
      <div className={variant === "table" ? styles.tableWrapper : undefined}>
        {variant === "detail" && (
          <SectionHeader title={`Contracts (${data?.totalCount ?? contractCount ?? contracts.length})`} />
        )}
        {renderContracts()}
      </div>
      {contractToEdit && (
        <EditContractModal
          builderId={builderId}
          contract={contractToEdit}
          builder={builder}
          onClose={() => setContractToEdit(undefined)}
          onChange={onChange}
        />
      )}
      {contractToDelete && (
        <DeleteContractModal
          builderId={builderId}
          contract={contractToDelete}
          onClose={() => setContractToDelete(undefined)}
          onChange={onChange}
        />
      )}
      {contractToDownloadBrochure && homePlan && canDownloadContractBrochure && (
        <DownloadBrochureModal
          open
          onClose={() => setContractToDownloadBrochure(undefined)}
          homePlan={{
            ...homePlan,
            customerInfo: contractToDownloadBrochure.customer ?? homePlan.customerInfo,
            totalPriceOverride: contractToDownloadBrochure.totalPriceOverride ?? homePlan.totalPriceOverride,
            salesStaff: contractToDownloadBrochure.salesStaff ?? homePlan.salesStaff,
          }}
          homePlanDiscounts={contractToDownloadBrochure.discounts}
          builder={builder}
          masterPlan={masterPlan}
          locationPlan={locationPlan}
          brochureSource={resolvedBrochureSource}
          location={location}
          lot={lot}
          lotDiscounts={lotDiscounts ?? []}
          displayRole={brochureUserRole ?? ""}
          isPublic={false}
        />
      )}
    </>
  );
}

function EditContractModal({
  builderId,
  builder,
  contract,
  onClose,
  onChange,
}: {
  builderId: string;
  builder?: ClientBuilder;
  contract: ClientHomePlanContractHydrated;
  onClose: () => void;
  onChange?: () => void;
}) {
  const { setUserNotification } = useAppContext();
  const { hasPermission } = useUserContext();
  const updateContract = useUpdateHomePlanContract(builderId);
  const existingDiscount = contract.discounts[0];
  const [showTotalPriceOverride, setShowTotalPriceOverride] = useState(
    contract.totalPriceOverride !== null && contract.totalPriceOverride !== undefined,
  );
  const [fields, setFields] = useState<HomePlanContractSchemaUpdateOne>({
    customerInfo: {
      name: contract.customer?.name,
      email: contract.customer?.email,
      phone: contract.customer?.phone,
      externalId: contract.customer?.externalId,
    },
    customerCustomFields: Object.fromEntries(
      Object.entries(contract.customerCustomFields ?? {}).map(([key, value]) => [key, value ?? ""]),
    ),
    status: contract.status,
    notes: contract.notes,
    totalPriceOverride: contract.totalPriceOverride ?? null,
    discount: existingDiscount
      ? { amount: existingDiscount.amount, description: existingDiscount.description }
      : undefined,
  });
  const canEditCustomer = hasPermission("EDIT_CUSTOMER_INFO");
  const email = fields.customerInfo?.email ?? "";
  const phone = fields.customerInfo?.phone ?? "";
  const initiallyHadContactMethod = !!contract.customer?.email || !!contract.customer?.phone;
  const contactError =
    initiallyHadContactMethod && !email.trim() && !phone.trim() ? "Email or phone is required." : null;
  const hasInvalidContact =
    !!contactError ||
    !!(email.trim() && validateEmail(email, false)) ||
    !!(phone.trim() && validateContractPhoneNumber(phone));
  const builderCustomFields = builder?.salesConfig?.publicShowroomCTA?.customFields ?? [];
  const customFieldKeys = [
    ...new Set(builderCustomFields.map((field) => field.name).concat(Object.keys(fields.customerCustomFields ?? {}))),
  ];
  const getCustomFieldConfig = (fieldName: string): BuilderPublicShowroomCTACustomFieldConfig =>
    builderCustomFields.find((field) => field.name === fieldName) ?? {
      id: `legacy-${fieldName}`,
      type: "TEXT",
      name: fieldName,
      required: false,
    };

  const setCustomerField = (field: "name" | "email" | "phone" | "externalId", value: string) => {
    const formattedValue = field === "phone" ? formatContractPhoneNumber(value) : value;
    const trimmedValue = formattedValue.trim();
    setFields({
      ...fields,
      customerInfo: {
        ...fields.customerInfo,
        [field]: field === "email" && !trimmedValue ? null : trimmedValue,
      },
    });
  };

  const handlePriceModeChange = (useTotalPriceOverride: boolean): void => {
    setShowTotalPriceOverride(useTotalPriceOverride);
    setFields(
      useTotalPriceOverride
        ? { ...fields, discount: null }
        : { ...fields, totalPriceOverride: null, discount: fields.discount ?? { amount: 0, description: "" } },
    );
  };

  const handleSubmit = async () => {
    if (hasInvalidContact) {
      return;
    }

    try {
      await updateContract.mutateAsync({ contractId: contract.id, fields });
      setUserNotification({ message: "Contract updated.", messageType: "success" });
      onChange?.();
      onClose();
    } catch {
      setUserNotification({ message: "Failed to update contract.", messageType: "error" });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="small"
      title="Edit contract"
      variants={["footerButtons", "bodyPadding"]}
      footer={
        <>
          <Button btnSize="large" btnStyle="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            btnSize="large"
            btnStyle="primary"
            isPending={updateContract.isPending}
            disabled={hasInvalidContact}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {hasPermission("VIEW_INTERNAL_IDS") && (
          <InputGroupText
            type="text"
            labelText="ID"
            inputValue={contract.customerId}
            placeholder="No Customer record created yet."
            tooltip="This is the internal ID of the customer record in Higharc. Only Super Admins can see this."
            disabled
          />
        )}
        <InputGroupText
          type="text"
          labelText="Name"
          inputValue={fields.customerInfo?.name ?? ""}
          disabled={!canEditCustomer}
          onChange={(value: string) => setCustomerField("name", value)}
        />
        <InputGroupText
          type="email"
          labelText="Email"
          inputValue={email}
          disabled={!canEditCustomer}
          validate={() => contactError ?? validateEmail(email, false)}
          onChange={(value: string) => setCustomerField("email", value)}
        />
        <InputGroupText
          type="tel"
          labelText="Phone"
          placeholder="Enter phone number"
          inputValue={phone}
          disabled={!canEditCustomer}
          validate={() => contactError ?? validateContractPhoneNumber(phone)}
          onChange={(value: string) => setCustomerField("phone", value)}
        />
        <InputGroupText
          type="text"
          labelText="External ID"
          inputValue={fields.customerInfo?.externalId ?? ""}
          disabled={!canEditCustomer}
          onChange={(value: string) => setCustomerField("externalId", value)}
        />
        {customFieldKeys.map((fieldName, index) => (
          <CustomField
            key={fieldName}
            field={getCustomFieldConfig(fieldName)}
            index={index}
            value={fields.customerCustomFields?.[fieldName] ?? ""}
            disabled={!canEditCustomer}
            onChange={(value: string) =>
              setFields({
                ...fields,
                customerCustomFields: { ...fields.customerCustomFields, [fieldName]: value },
              })
            }
          />
        ))}
        <InputGroupSelect
          labelText="Status"
          options={STATUS_OPTIONS}
          value={fields.status}
          onChange={(value) => setFields({ ...fields, status: value as HomePlanContractStatus })}
        />
        <div className={styles.totalPriceOverrideToggle}>
          <label>Use Total Price Override</label>
          <Toggle
            isTrue={showTotalPriceOverride}
            onChange={handlePriceModeChange}
            disabled={
              !hasPermission("EDIT_HOME_PLAN_DISCOUNT") && !hasPermission("EDIT_HOME_PLAN_TOTAL_PRICE_OVERRIDE")
            }
            variants="small"
          />
        </div>
        {showTotalPriceOverride ? (
          <InputGroupNumber
            labelText="Total Price Override"
            placeholder="Enter total price override"
            inputValue={fields.totalPriceOverride ?? undefined}
            disabled={!hasPermission("EDIT_HOME_PLAN_TOTAL_PRICE_OVERRIDE")}
            onChange={(value: number | null) => setFields({ ...fields, totalPriceOverride: value })}
            tooltip="This will be the fixed price of the Home Plan."
          />
        ) : (
          <>
            <InputGroupNumber
              labelText="Price Adjustment Amount"
              placeholder="0"
              inputValue={fields.discount?.amount ?? 0}
              disabled={!hasPermission("EDIT_HOME_PLAN_DISCOUNT")}
              onChange={(value: number | null) =>
                setFields({
                  ...fields,
                  discount: { ...fields.discount, amount: value ?? 0 },
                })
              }
            />
            <InputGroupText
              type="text"
              labelText="Price Adjustment Description"
              placeholder="Enter a description for the adjustment"
              inputValue={fields.discount?.description ?? ""}
              disabled={!hasPermission("EDIT_HOME_PLAN_DISCOUNT")}
              onChange={(value: string) =>
                setFields({
                  ...fields,
                  discount: { ...fields.discount, description: value },
                })
              }
            />
          </>
        )}
        <InputGroupText
          type="textarea"
          labelText="Notes"
          inputValue={fields.notes ?? ""}
          maxLength={512}
          onChange={(value: string) => setFields({ ...fields, notes: value })}
        />
      </div>
    </Modal>
  );
}

function DeleteContractModal({
  builderId,
  contract,
  onClose,
  onChange,
}: {
  builderId: string;
  contract: ClientHomePlanContractHydrated;
  onClose: () => void;
  onChange?: () => void;
}) {
  const { setUserNotification } = useAppContext();
  const deleteContract = useDeleteHomePlanContract(builderId);

  const handleDelete = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await deleteContract.mutateAsync(contract.id);
      setUserNotification({ message: "Contract deleted.", messageType: "success" });
      onChange?.();
      onClose();
    } catch (error) {
      // A 409 means the server refused because this is the plan's last presented contract (see
      // presentedContracts above) — distinct from a transient failure, so the recovery differs too.
      const message =
        error instanceof FetchError && error.status === 409
          ? "This is the plan's last contract and can't be deleted. Add another contract first."
          : "This contract could not be deleted. Refresh the page and try again.";
      setUserNotification({ message, messageType: "error" });
    }
  };

  return (
    <ConfirmDeleteModal
      onClose={onClose}
      onConfirm={handleDelete}
      entityType="Contract"
      isDeleting={deleteContract.isPending}
      requireEntityNameEntry={false}
      message="Delete this contract? Its customer, status, and notes are removed from the Home Plan."
    />
  );
}
